import { FileText, Download, ExternalLink, Pencil } from "lucide-react";
import { QuoteEstimate } from "../QuoteEstimate";

export function QuoteTab({ quoteId, data, onSaved }) {
  return (
    <div className="space-y-6" data-testid="jobfile-quote-tab">
      <div className="border border-[#F0D5B4] bg-[#FBF3EC] p-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-[#6B7280]">
          <Pencil size={12} className="inline mr-1.5 text-[#C79A3B]" />
          Quotes are editable from day one — adjust line items below (every save is a numbered revision) or re-open the configurator.
        </p>
        {data.route === "pd" && (
          <a href={`/quote?edit=${data.quote_id}`} target="_blank" rel="noreferrer" data-testid="quote-detail-edit-link"
            className="text-xs font-bold text-[#C79A3B] hover:text-[#A87E28] inline-flex items-center gap-1 shrink-0">
            <ExternalLink size={12} /> Edit in configurator
          </a>
        )}
      </div>

      <QuoteEstimate quoteId={quoteId} data={data} onSaved={onSaved} />

      <div>
        <div className="text-xs font-black uppercase tracking-widest text-[#12263A] mb-3">Visuals</div>
        {data.render_kinds && data.render_kinds.length > 0 ? (
          <div className={`grid gap-3 ${data.render_kinds.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid="quote-detail-visuals">
            {data.render_kinds.map(k => (
              <figure key={k} className="border border-[#E5E7EB] bg-white overflow-hidden">
                <img src={data.renders[k]} alt={`${data.reference} — ${k}`} className="w-full h-auto" data-testid={`quote-detail-render-${k}`}
                  onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                <figcaption className="text-[10px] uppercase tracking-widest text-[#6B7280] px-3 py-2">{k}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#6B7280]">No visuals available for this quote yet.</div>
        )}
      </div>

      <div>
        <div className="text-xs font-black uppercase tracking-widest text-[#12263A] mb-3">Plan</div>
        <div className="space-y-4" data-testid="quote-detail-plan">
          {data.route === "planning" && data.plan_files && data.plan_files.length > 0 && (
            <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-detail-plan-files">
              <div className="label-eyebrow mb-3">Uploaded architect drawings ({data.plan_files.length})</div>
              <ul className="space-y-2">
                {data.plan_files.map(f => (
                  <li key={f.index} className="flex items-center justify-between gap-2 border border-[#F0EAE0] px-3 py-2" data-testid={`quote-detail-plan-file-${f.index}`}>
                    <div className="flex items-center gap-2 text-sm text-[#12263A] min-w-0">
                      <FileText size={14} className="text-[#C79A3B] shrink-0" />
                      <span className="truncate">{f.filename}</span>
                    </div>
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-xs de-link inline-flex items-center gap-1 shrink-0" data-testid={`quote-detail-plan-file-${f.index}-download`}>
                      View <Download size={11} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.plan_sketch_url ? (
            <figure className="border border-[#E5E7EB] bg-white overflow-hidden">
              <img src={data.plan_sketch_url} alt={`${data.reference} — plan sketch`} className="w-full h-auto"
                data-testid="quote-detail-plan-sketch"
                onError={(e) => { e.target.parentElement.style.display = "none"; }} />
              <figcaption className="text-[10px] uppercase tracking-widest text-[#6B7280] px-3 py-2">Auto-generated plan sketch (current saved configuration)</figcaption>
            </figure>
          ) : (data.route === "planning" && (!data.plan_files || data.plan_files.length === 0)) ? (
            <div className="border border-dashed border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#6B7280]">No plan uploads found for this client — they may have used a link-only submission.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
